import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IBulkAddState } from 'src/app/inventory/interfaces/bulk-add-state.interface';
import { IUserMessageIndexModel } from 'src/app/messages/interfaces/user-message-index-model.interface';
import { endpoints } from '../../constants/endpoints';
import * as Act from './user-message.actions';

export interface UserMessageStateModel {
  loading: boolean;
  success: boolean;
  latestUnread: IUserMessageIndexModel;
  unreadTotal: number;
  allMessages: IUserMessageIndexModel[];
}

@State<UserMessageStateModel>({
  name: 'userMessage',
})

@UntilDestroy()
@Injectable()
export class UserMessageState  {

  constructor(
    private http: HttpClient,
  ) {
  }

  @Selector()
  static loading(state: UserMessageStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: UserMessageStateModel): boolean {
    return state.success;
  }

  @Selector()
  static latestUnread(state: UserMessageStateModel): IUserMessageIndexModel {
    return state.latestUnread;
  }

  @Selector()
  static unreadTotal(state: UserMessageStateModel): number {
    return state.unreadTotal;
  }

  @Selector()
  static allMessages(state: UserMessageStateModel): IUserMessageIndexModel[] {
    return state.allMessages;
  }

  @Action(Act.GetUnreadMessagesSummary)
  getUnreadMessagesSummary(ctx: StateContext<UserMessageStateModel>, {userId}: Act.GetUnreadMessagesSummary): Observable<IBulkAddState> {
    ctx.patchState({loading: true});

    return this.http.get(endpoints.userMessage.getUnreadMessagesSummary + '/' + userId)
    .pipe(
      tap((responseData: any) => {
        ctx.patchState({
          loading: false,
          latestUnread: responseData.value?.latestUnread,
          unreadTotal: responseData.value?.unreadTotal
        });
      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.GetUnreadMessagesCount)
  getUnreadMessagesCount(ctx: StateContext<UserMessageStateModel>, {userId}: Act.GetUnreadMessagesCount): Observable<IBulkAddState> {
    ctx.patchState({loading: true});

    return this.http.get(endpoints.userMessage.getUnreadMessagesCount + '/' + userId)
    .pipe(
      tap((responseData: any) => {
        ctx.patchState({
          loading: false,
          unreadTotal: responseData.value
        });
      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.MarkMessageRead)
  markMessageRead(ctx: StateContext<UserMessageStateModel>, {userId, messageId}: Act.MarkMessageRead): Observable<IBulkAddState> {
    ctx.patchState({loading: true});

    return this.http.patch(endpoints.userMessage.markMessageRead, {userId, messageId})
    .pipe(
      tap((responseData: any) => {
       
        ctx.patchState({
          loading: false,
        });

        if (responseData.success) {
          ctx.patchState({
            unreadTotal: ctx.getState()?.unreadTotal - 1,
            allMessages: ctx.getState()?.allMessages?.map(msg => msg.messageId === messageId ? {...msg, readOn: new Date()} : msg),
            latestUnread: ctx.getState()?.latestUnread?.messageId === messageId ? null : ctx.getState()?.latestUnread
          });
        }

      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.ClearUnreadMessageFromState)
  clearUnreadMessage(ctx: StateContext<UserMessageStateModel>, {}: Act.ClearUnreadMessageFromState): void {
   ctx.patchState({latestUnread: null});
  }

  @Action(Act.GetAllMessages)
  getAllMessages(ctx: StateContext<UserMessageStateModel>, {userId}: Act.GetAllMessages): Observable<IBulkAddState> {
    ctx.patchState({loading: true});

    return this.http.get(endpoints.userMessage.getAllMessages + '/' + userId)
    .pipe(
      tap((responseData: any) => {
        ctx.patchState({
          loading: false,
          allMessages: responseData.value
        });
      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.DeleteUserMessage)
  deleteUserMessage(ctx: StateContext<UserMessageStateModel>, {userMessageId, messageId}: Act.DeleteUserMessage): Observable<IBulkAddState> {
    ctx.patchState({loading: true});

    return this.http.delete(endpoints.userMessage.deleteUserMessage + '/' + userMessageId)
    .pipe(
      tap((responseData: any) => {
       
        ctx.patchState({
          loading: false,
        });

        if (responseData.success) {
          ctx.patchState({
            unreadTotal: ctx.getState()?.unreadTotal - 1,
            allMessages: ctx.getState()?.allMessages?.filter(msg => msg.userMessageId !== userMessageId),
            latestUnread: ctx.getState()?.latestUnread?.messageId === messageId ? null : ctx.getState()?.latestUnread
          });
        }

      }),
      catchError((err) => {
        ctx.patchState({loading: false});
        return of(null);
      }),
    );
  }

  @Action(Act.ClearUserMessageState)
  clearUserMessageState(ctx: StateContext<UserMessageStateModel>, {}: Act.ClearUserMessageState): void {
    ctx.setState(null);
  }
  


}
